/* Booking helpers: URL state, cart encoding, smart suggestions, ICS. */

export function encodeCart(cart) {
  return cart.map((i) => `${i.room_type_id}.${i.guests}.${i.board && i.board !== "standard" ? i.board : "s"}`).join("_");
}

export function decodeCart(str) {
  if (!str) return [];
  return str
    .split(/[_,]/)
    .map((part) => {
      const [room_type_id, g, b] = part.split(/[.:]/);
      const guests = Math.max(1, parseInt(g, 10) || 1);
      return room_type_id ? { room_type_id, guests, board: b && b !== "s" ? b : "standard" } : null;
    })
    .filter(Boolean);
}

/** Drop unknown room types and clamp guests/board to what the room type allows. */
export function sanitizeCart(cart, byId) {
  if (!byId || !Object.keys(byId).length) return cart;
  return cart
    .filter((i) => byId[i.room_type_id])
    .map((i) => {
      const rt = byId[i.room_type_id];
      const boardOk = i.board === "standard" || (rt.board_options || []).some((b) => b.id === i.board);
      return { ...i, guests: Math.min(Math.max(1, i.guests), rt.max_guests), board: boardOk ? i.board : "standard" };
    });
}

export function buildBookingUrl({ ci, co, adults = 2, children = 0, dog = false, cart = [], step } = {}) {
  const p = new URLSearchParams();
  if (ci) p.set("ci", ci);
  if (co) p.set("co", co);
  p.set("a", String(adults));
  if (children) p.set("c", String(children));
  if (dog) p.set("dog", "1");
  if (cart.length) p.set("r", encodeCart(cart));
  if (step) p.set("step", String(step));
  return `/bestill?${p.toString()}`;
}

const LAST_KEY = "ffh_last_search";
export function saveLastSearch(s) {
  try { localStorage.setItem(LAST_KEY, JSON.stringify(s)); } catch (e) { /* ignore */ }
}
export function loadLastSearch() {
  try { return JSON.parse(localStorage.getItem(LAST_KEY) || "null"); } catch (e) { return null; }
}

/** price for one room type with g guests from /availability payload */
export function priceFor(av, g, board) {
  if (!av) return null;
  if (board && board !== "standard" && av.board_quotes?.[board]) return av.board_quotes[board].subtotal;
  return av.quotes?.[String(g)]?.subtotal ?? null;
}

/**
 * Smart suggestions: cheapest combination of up to 3 rooms covering the party.
 * Returns [{key, label, items:[{room_type_id, guests}], total}]
 */
export function suggestCombos(roomTypes, availability, party, dog) {
  if (!availability || !roomTypes?.length || party < 1) return [];
  const avById = Object.fromEntries(availability.room_types.map((a) => [a.room_type_id, a]));
  const types = roomTypes.filter((rt) => (avById[rt.id]?.available || 0) > 0);
  const results = [];
  const maxRooms = party <= 4 ? 2 : 3;

  function evalCombo(combo) {
    // combo: array of room types (with repetition)
    const counts = {};
    for (const rt of combo) counts[rt.id] = (counts[rt.id] || 0) + 1;
    for (const id in counts) if (counts[id] > (avById[id]?.available || 0)) return null;
    if (dog && !combo.some((rt) => rt.dogs_allowed)) return null;
    const cap = combo.reduce((s, rt) => s + rt.max_guests, 0);
    if (cap < party || combo.length > party) return null;
    // best allocation (brute force)
    let best = null;
    const alloc = new Array(combo.length).fill(1);
    function rec(i, remaining) {
      if (i === combo.length) {
        if (remaining !== 0) return;
        const total = combo.reduce((s, rt, k) => s + (priceFor(avById[rt.id], alloc[k]) ?? Infinity), 0);
        if (!best || total < best.total) best = { total, alloc: [...alloc] };
        return;
      }
      const left = combo.length - i - 1;
      for (let g = 1; g <= combo[i].max_guests; g++) {
        if (remaining - g < left) break;
        alloc[i] = g;
        rec(i + 1, remaining - g);
      }
    }
    rec(0, party);
    if (!best || !Number.isFinite(best.total)) return null;
    return { items: combo.map((rt, k) => ({ room_type_id: rt.id, guests: best.alloc[k], board: "standard" })), total: best.total };
  }

  function combos(start, size, acc) {
    if (acc.length === size) {
      const r = evalCombo(acc);
      if (r) results.push(r);
      return;
    }
    for (let i = start; i < types.length; i++) combos(i, size, [...acc, types[i]]);
  }
  for (let size = 1; size <= maxRooms; size++) combos(0, size, []);
  if (!results.length) return [];
  results.sort((a, b) => a.total - b.total || a.items.length - b.items.length);
  const cheapest = { key: "value", ...results[0] };
  const out = [cheapest];
  const sea = results
    .filter((r) => r.items.every((i) => roomTypes.find((rt) => rt.id === i.room_type_id)?.badges?.includes("sea_view") || roomTypes.find((rt) => rt.id === i.room_type_id)?.max_guests === 1))
    .filter((r) => r.items.some((i) => roomTypes.find((rt) => rt.id === i.room_type_id)?.badges?.includes("sea_view")))
    .sort((a, b) => a.items.length - b.items.length || a.total - b.total)[0];
  if (sea && encodeCart(sea.items) !== encodeCart(cheapest.items)) out.push({ key: "sea", ...sea });
  const fewest = [...results].sort((a, b) => a.items.length - b.items.length || a.total - b.total)[0];
  if (fewest && !out.some((o) => encodeCart(o.items) === encodeCart(fewest.items)) && fewest.items.length < cheapest.items.length) {
    out.push({ key: "fewest", ...fewest });
  }
  return out.slice(0, 3);
}

/** Distribute the party across cart rooms: fill base guests first, then extra beds. */
export function allocateGuests(cart, party, byId) {
  if (!cart.length) return cart;
  const caps = cart.map((i) => byId[i.room_type_id]?.max_guests || 1);
  const out = cart.map(() => 1);
  let remaining = Math.max(party, cart.length) - cart.length;
  for (let pass = 0; pass < 2 && remaining > 0; pass++) {
    for (let k = 0; k < cart.length && remaining > 0; k++) {
      const rt = byId[cart[k].room_type_id];
      const target = pass === 0 ? Math.min(rt?.base_guests || 2, caps[k]) : caps[k];
      const add = Math.min(target - out[k], remaining);
      if (add > 0) { out[k] += add; remaining -= add; }
    }
  }
  return cart.map((i, k) => ({ ...i, guests: out[k] }));
}

export function cartCapacity(cart, byId) {
  return cart.reduce((s, i) => s + (byId[i.room_type_id]?.max_guests || 0), 0);
}

/** cart -> {room_type_id: qty} */
export function cartCounts(cart) {
  const c = {};
  cart.forEach((i) => { c[i.room_type_id] = (c[i.room_type_id] || 0) + 1; });
  return c;
}

/** set quantity of a room type in cart (adds/removes instances at the end) */
export function setCartQty(cart, roomTypeId, qty, rt) {
  const others = cart.filter((i) => i.room_type_id !== roomTypeId);
  const mine = cart.filter((i) => i.room_type_id === roomTypeId).slice(0, qty);
  while (mine.length < qty) mine.push({ room_type_id: roomTypeId, guests: Math.min(2, rt?.max_guests || 2), board: "standard" });
  return [...others, ...mine];
}

const LAST_BOOKING_KEY = "ffh_last_booking";
export function saveLastBooking(b) {
  try { sessionStorage.setItem(LAST_BOOKING_KEY, JSON.stringify(b)); } catch (e) { /* ignore */ }
}
export function loadLastBooking() {
  try { return JSON.parse(sessionStorage.getItem(LAST_BOOKING_KEY) || "null"); } catch (e) { return null; }
}

function icsDate(iso) {
  return iso.replace(/-/g, "");
}

export function downloadIcs(booking, roomNames, lang = "nn") {
  const summary = lang === "en" ? `Stay at Rosendal Fjordhotel (${booking.ref})` : `Opphald på Rosendal Fjordhotel (${booking.ref})`;
  const rooms = booking.rooms.map((r) => `${roomNames[r.room_type_id] || r.room_type_id} (${r.guests})`).join(", ");
  const desc = (lang === "en"
    ? `Booking ${booking.ref}. Rooms: ${rooms}. Check-in from 15:00, check-out before 12:00. Phone +47 53 48 80 00.`
    : `Bestilling ${booking.ref}. Rom: ${rooms}. Innsjekk frå kl. 15.00, utsjekk før kl. 12.00. Telefon 53 48 80 00.`).replace(/,/g, "\\,");
  const now = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Rosendal Fjordhotel//Booking//NN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${booking.ref}@rosendal-fjordhotell.no`,
    `DTSTAMP:${now}`,
    `DTSTART;VALUE=DATE:${icsDate(booking.check_in)}`,
    `DTEND;VALUE=DATE:${icsDate(booking.check_out)}`,
    `SUMMARY:${summary}`,
    "LOCATION:Rosendal Fjordhotel\\, Rosendalsvegen 46\\, 5470 Rosendal",
    `DESCRIPTION:${desc}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `rosendal-fjordhotell-${booking.ref}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export const WISHES = [
  { id: "step_free", nn: "Rom utan trapper", en: "Step-free room" },
  { id: "cot", nn: "Barneseng", en: "Baby cot" },
  { id: "early_checkin", nn: "Tidleg innsjekk", en: "Early check-in" },
  { id: "late_checkout", nn: "Sein utsjekk", en: "Late check-out" },
  { id: "dinner_table", nn: "Bord til middag", en: "Dinner table" },
  { id: "allergies", nn: "Allergiar / kost", en: "Allergies / diet" },
  { id: "sea_view_wish", nn: "Sjøutsikt om mogleg", en: "Sea view if possible" },
  { id: "quiet", nn: "Roleg rom", en: "Quiet room" },
];

export const ARRIVALS = ["15–16", "16–18", "18–20", "20–22", "22+"];

export const BADGE_LABELS = {
  sea_view: { nn: "Fjordutsikt", en: "Fjord view" },
  dog: { nn: "Kjæledyr etter avtale", en: "Pets by arrangement" },
  step_free: { nn: "Utan trapper", en: "Step-free" },
  family: { nn: "Familie", en: "Family" },
  best_value: { nn: "Rimelegast", en: "Best value" },
  river: { nn: "Ved elva", en: "By the river" },
};
